import { formatDate } from "@angular/common";
import { AfterViewInit, Component, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ConfirmationService, MessageService } from "primeng/api";
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from "primeng/dynamicdialog";
import {
  AsReportedSecurityBreakdown,
  AsReportedSecurityBreakdownTags,
  Filing,
} from "src/api/models";
import {
  AsReportedSecurityBreakdownService,
  FilingService,
} from "src/api/services";
import { PlanAction, TransactionType } from "../../../../../api/models";
import { EditSecurityTagComponent } from "../edit-security-tag/edit-security-tag.component";
import { AddTextComponent } from "../add-text/add-text.component";

@Component({
  selector: "app-processed-edit",
  // imports: [],
  templateUrl: "./processed-edit.component.html",
  styleUrl: "./processed-edit.component.css",
  standalone: false,
})
export class ProcessedEditComponent implements OnInit, AfterViewInit {
  first = 0;
  rows = 10;
  selectedPlans!: any[];
  filing: Filing | any = {};

  // Main form group that contains all trading plans
  formMainGroup: FormGroup;
  transactionTypeOptions = TransactionType.values().map((type) => ({
    label: this.formatTransactionType(type),
    value: type,
  }));
  entityTypeOptions = [
    { label: "INDIVIDUAL", value: "INDIVIDUAL" },
    { label: "TRUST", value: "TRUST" },
    { label: "LLC", value: "LLC" },
    { label: "SPOUSE", value: "SPOUSE" },
    { label: "PARTNERSHIP", value: "PARTNERSHIP" },
    { label: "FUND", value: "FUND" },
    { label: "OTHER", value: "OTHER" },
  ];
  planActionOptions = PlanAction.values().map((type) => ({
    label: this.formatPlanAction(type),
    value: type,
  }));
  showMessage = false;
  messageText = "At least one security is required in trading plan.";
  hasPersistedBreakdown = false;
  private originalCloseFn?: (result?: any) => void;
  private closeGuardInitialized = false;
  isSaving = false;
  constructor(
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private _filingService: FilingService,
    private _asReportedSecurityBreakdownService: AsReportedSecurityBreakdownService,
    private _confirmationService: ConfirmationService,
    private _messageService: MessageService,
    private fb: FormBuilder,
    public dialogService: DialogService
  ) {
    this.formMainGroup = this.fb.group({
      tradingPlans: this.fb.array([]),
      followUp: [false],
      followUpComment: [""],
    });
  }

  // Getter for easy access to trading plans form array
  get tradingPlans(): FormArray {
    return this.formMainGroup.get("tradingPlans") as FormArray;
  }

  ngOnInit(): void {
    this.filing = this.config.data.filing;
    this.selectedPlans = this.config.data.selectedPlan;
    this.updatePersistedBreakdownFlagFromSelectedPlans();
    this.setupCloseGuard();
  }

  formatTransactionType(type: TransactionType): string {
    switch (type) {
      case "BUY":
        return "Buy";
      case "SELL":
        return "Sell";
      case "OTHER":
        return "Other";
      default:
        return type;
    }
  }

  formatPlanAction(type: PlanAction): string {
    switch (type) {
      case "ADOPTION":
        return "Adoption";
      case "TERMINATION":
        return "Termination";
      case "AMENDMENT":
        return "Amendment";
      default:
        return type;
    }
  }

  ngAfterViewInit(): void {
    this.onEditClick();
  }

  onEditClick() {
    if (this.selectedPlans.length > 0) {
      // Clear existing form groups
      while (this.tradingPlans.length !== 0) {
        this.tradingPlans.removeAt(0);
      }

      for (let plan of this.selectedPlans) {
        const securityBreakdowns = Array.isArray(
          plan.asReportedSecurityBreakdowns
        )
          ? plan.asReportedSecurityBreakdowns
          : [];

        // Add new trading plan form group
        this.tradingPlans.push(
          this.createTradingPlanFormGroup(plan, securityBreakdowns)
        );
      }
    }
    if (this.filing?.followUpComment) {
      this.formMainGroup.patchValue({
        followUp: true,
        followUpComment: this.filing.followUpComment,
      });
    }
  }

  createTradingPlanFormGroup(plan: any, securityBreakdowns: any[]): FormGroup {
    const formGroup = this.fb.group({
      tradingPlanId: [plan.id || plan.tradingPlanId || null],
      insiderId: [plan.asReportedInsider?.insiderId  || null],
      insiderName: [
        plan.asReportedInsider?.asReportedInsiderName || "",
        Validators.required,
      ],
      asReportedReportingEntityName: [
        plan.asReportedInsider.asReportedReportingEntityName || null,
      ],

      asReportedReportingEntityType: [
        plan.asReportedInsider.asReportedReportingEntityType || null,
      ],
      asReportedInsiderRoleInEntity: [
        plan.asReportedInsider.asReportedInsiderRoleInEntity || null,
      ],
      planAction: [plan.planAction || "", Validators.required],
      relation: [plan.relationshipToIssuer || ""],

      durationDays: [
        plan.durationDays || "",
        [Validators.pattern(/^\d+$/), Validators.min(0)],
      ],
      asReportedPlanText: [plan.asReportedPlanText || "", Validators.required],
      insiderComment: [plan.asReportedInsider.insiderComment || ""],

      adoptionDate: [plan.adoptionDate ? new Date(plan.adoptionDate) : null],
      effectiveDate: [plan.effectiveDate ? new Date(plan.effectiveDate) : null],
      amendmentDate: [plan.amendmentDate ? new Date(plan.amendmentDate) : null],
      terminationDate: [
        plan.terminationDate ? new Date(plan.terminationDate) : null,
      ],
      expirationDate: [
        plan.expirationDate ? new Date(plan.expirationDate) : null,
      ],
      modificationComment: [plan.modificationComment || null],
      modifiedFields: [plan.modifiedFields || null],
      isEstimatedAdoptionDate: [plan.isEstimatedAdoptionDate ?? null],
      isEstimatedTerminationDate: [plan.isEstimatedTerminationDate ?? null],
      isEstimatedAmendedDate: [plan.isEstimatedAmendedDate ?? null],
      securityBreakdowns: this.fb.array([]),
    });
    // Add conditional validators based on planAction
    formGroup.get("planAction")?.valueChanges.subscribe((action) => {
      const adoptionDateControl = formGroup.get("adoptionDate");
      const terminationDateControl = formGroup.get("terminationDate");
      const amendmentDateControl = formGroup.get("amendmentDate");

      // Reset validators first
      adoptionDateControl?.clearValidators();
      terminationDateControl?.clearValidators();
      amendmentDateControl?.clearValidators();

      // Set validators based on action
      switch (action) {
        case "ADOPTION":
          adoptionDateControl?.setValidators(Validators.required);
          break;
        case "TERMINATION":
          terminationDateControl?.setValidators(Validators.required);
          break;
        case "AMENDMENT":
          amendmentDateControl?.setValidators(Validators.required);
          break;
      }

      // Update control validity
      adoptionDateControl?.updateValueAndValidity();
      terminationDateControl?.updateValueAndValidity();
      amendmentDateControl?.updateValueAndValidity();
    });

    // Trigger initial validation
    formGroup.get("planAction")?.updateValueAndValidity();
    // Add security breakdowns
    const breakdownArray = formGroup.get("securityBreakdowns") as FormArray;
    for (let breakdown of securityBreakdowns) {
      breakdownArray.push(this.createBreakdownFormGroup(breakdown, plan));
    }

    return formGroup;
  }

  createBreakdownFormGroup(breakdown: any, plan: any): FormGroup {
    return this.fb.group({
      tradingPlanId: [plan.id || plan.tradingPlanId || null],
      securityType: [
        breakdown.asReportedSecurity?.asReportedSecurityType || "",
        Validators.required,
      ],
      transactionType: [breakdown.transactionType || "", Validators.required],
      numberOfShares: [breakdown.numberOfShares || null, [Validators.min(0)]],
      notionalValue: [breakdown.notionalValue || null],
      // durationDays: [plan.durationDays || null],
      // followUp: [false],
      tradingConditions: [plan.tradingConditions || ""],
      earlyTerminationConditions: [plan.earlyTerminationConditions || ""],
      sharesDescription: [breakdown.sharesDescription || ""],
      securityBreakdownTags: [breakdown.securityBreakdownTags || {}],
    });
  }

  addTradingPlan() {
    if (this.tradingPlans.length > 0) {
      // Last plan get karo
      const lastPlan = this.tradingPlans.at(this.tradingPlans.length - 1).value;
      this.tradingPlans.push(this.createClonedTradingPlanFormGroup(lastPlan));
    } else {
      // Pehla plan hamesha empty
      this.tradingPlans.push(this.createEmptyTradingPlanFormGroup());
    }
  }

  createEmptyTradingPlanFormGroup(): FormGroup {
    const formGroup = this.fb.group({
      tradingPlanId: [null],
      insiderName: ["", Validators.required],
      asReportedReportingEntityName: null,
      asReportedReportingEntityType: null,
      asReportedInsiderRoleInEntity: null,
      planAction: ["", Validators.required],
      relation: [""],
      adoptionDate: null,
      effectiveDate: null,
      durationDays: ["", [Validators.pattern(/^\d+$/), Validators.min(0)]],
      terminationDate: null,
      amendmentDate: null,
      expirationDate: null,
      asReportedPlanText: ["", Validators.required],
      insiderComment: [""],

      modificationComment: [null],
      modifiedFields: [null],
      isEstimatedAdoptionDate: [null],
      isEstimatedTerminationDate: [null],
      isEstimatedAmendedDate: [null],
      securityBreakdowns: this.fb.array([this.createEmptyBreakdownFormGroup()]),
    });

    // Add the same conditional validation logic
    formGroup.get("planAction")?.valueChanges.subscribe((action) => {
      const adoptionDateControl = formGroup.get("adoptionDate");
      const terminationDateControl = formGroup.get("terminationDate");
      const amendmentDateControl = formGroup.get("amendmentDate");

      adoptionDateControl?.clearValidators();
      terminationDateControl?.clearValidators();
      amendmentDateControl?.clearValidators();

      switch (action) {
        case "ADOPTION":
          adoptionDateControl?.setValidators(Validators.required);
          break;
        case "TERMINATION":
          terminationDateControl?.setValidators(Validators.required);
          break;
        case "AMENDMENT":
          amendmentDateControl?.setValidators(Validators.required);
          break;
      }

      adoptionDateControl?.updateValueAndValidity();
      terminationDateControl?.updateValueAndValidity();
      amendmentDateControl?.updateValueAndValidity();
    });

    return formGroup;
  }
  createClonedTradingPlanFormGroup(plan: any): FormGroup {
    return this.fb.group({
      tradingPlanId: [null], // new plan, no ID
      insiderName: [plan.insiderName || "", Validators.required],
      asReportedReportingEntityName: plan.asReportedReportingEntityName || null,
      asReportedReportingEntityType: plan.asReportedReportingEntityType || null,
      asReportedInsiderRoleInEntity: plan.asReportedInsiderRoleInEntity || null,
      planAction: [plan.planAction || "", Validators.required],
      relation: [plan.relation || ""],
      adoptionDate: plan.adoptionDate ? new Date(plan.adoptionDate) : null,
      effectiveDate: plan.effectiveDate ? new Date(plan.effectiveDate) : null,
      durationDays: [
        plan.durationDays || "",
        [Validators.pattern(/^\d+$/), Validators.min(0)],
      ],
      terminationDate: plan.terminationDate
        ? new Date(plan.terminationDate)
        : null,
      amendmentDate: plan.amendmentDate ? new Date(plan.amendmentDate) : null,
      expirationDate: plan.expirationDate
        ? new Date(plan.expirationDate)
        : null,
      asReportedPlanText: [plan.asReportedPlanText || "", Validators.required],
      insiderComment: [plan.insiderComment || ""],

      modificationComment: [plan.modificationComment || null],
      modifiedFields: [plan.modifiedFields || null],
      isEstimatedAdoptionDate: [plan.isEstimatedAdoptionDate ?? null],
      isEstimatedTerminationDate: [plan.isEstimatedTerminationDate ?? null],
      isEstimatedAmendedDate: [plan.isEstimatedAmendedDate ?? null],

      // ⚡️ Always empty breakdowns
      securityBreakdowns: this.fb.array([]),
    });
  }

  createEmptyBreakdownFormGroup(tradingPlanId: any = null): FormGroup {
    return this.fb.group({
      tradingPlanId: [tradingPlanId],
      securityType: ["", Validators.required],
      transactionType: ["", Validators.required],
      numberOfShares: [null, [Validators.min(0)]],
      notionalValue: [null],
      // durationDays: [null],
      // followUp: [false],
      tradingConditions: [""],
      earlyTerminationConditions: [""],
      sharesDescription: [""],
      securityBreakdownTags: this.fb.control({}),
    });
  }

  addSecurityBreakdown(index: number) {
    this.showMessage = false;

    const breakdownArray = this.getBreakdownControls(index);
    const planForm = this.tradingPlans.at(index) as FormGroup;

    // Get the tradingPlanId from the current plan form
    const planId = planForm.get("tradingPlanId")?.value;

    const newBreakdown = this.createEmptyBreakdownFormGroup(planId);
    breakdownArray.push(newBreakdown);

    // Mark the form as dirty since we've made a change
    this.formMainGroup.markAsDirty();
  }
  getBreakdownControls(index: number): FormArray {
    return this.tradingPlans.at(index).get("securityBreakdowns") as FormArray;
  }

  removeSecurityBreakdown(rowIndex: number, breakdownIndex: number): void {
    const breakdownArray = this.getBreakdownControls(rowIndex);
    const breakdownControl = breakdownArray.at(breakdownIndex);
    const tradingPlanId = breakdownControl.value.tradingPlanId;
    if (!tradingPlanId) {
      breakdownArray.removeAt(breakdownIndex);
      return;
    }

    this._confirmationService.confirm({
      message: `<div class="font-semibold text-base">
            Are you sure you want to delete this <strong>security breakdown</strong>?<br/>
            This action <u>cannot be undone</u>.
          </div>`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      rejectButtonStyleClass: "p-button-secondary p-button-sm",
      acceptButtonStyleClass: "p-button-danger p-button-sm",
      closable: true,
      rejectVisible: true,
      accept: () => {
        const originalPlan = this.selectedPlans[rowIndex];
        const originalBreakdown =
          originalPlan?.asReportedSecurityBreakdowns?.[breakdownIndex];

        if (originalBreakdown?.asReportedSecurityBreakdownId) {
          this._asReportedSecurityBreakdownService
            .deleteAsReportedSecurityBreakdown({
              asReportedSecurityBreakdownId:
                originalBreakdown.asReportedSecurityBreakdownId,
              reason: "Incorrect plan created by LLM",
            })
            .subscribe({
              next: (response: boolean) => {
                if (response) {
                  breakdownArray.removeAt(breakdownIndex);
                  this.removePersistedBreakdownReference(
                    originalPlan,
                    breakdownIndex
                  );
                  this._messageService.add({
                    severity: "success",
                    summary: "Deleted",
                    detail: "Breakdown deleted successfully",
                  });
                } else {
                  this._messageService.add({
                    severity: "error",
                    summary: "Failed",
                    detail: "Breakdown could not be deleted",
                  });
                }
              },
              error: (err: any) => {
                this._messageService.add({
                  severity: "error",
                  summary: "Error",
                  detail: "An error occurred while deleting",
                });
                console.error(err);
              },
            });
        } else {
          // If no ID exists, just remove from the form
          breakdownArray.removeAt(breakdownIndex);
          this._messageService.add({
            severity: "warn",
            summary: "Removed",
            detail: "Breakdown removed from form (not deleted from server)",
          });
        }
      },
    });
  }
  removeTradingPlan(index: number): void {
    this.tradingPlans.removeAt(index);
  }
  validateForm() {
    let isValid = true;

    this.markFormGroupTouched(this.formMainGroup); // Touch all fields for validation UI
    const formValue = this.formMainGroup.value;
    // Check if any trading plan has empty securityBreakdowns
    const hasEmptySecurity = formValue.tradingPlans.some(
      (plan: any, index: number) => {
        const breakdowns = plan.securityBreakdowns;
        if (!breakdowns || breakdowns.length === 0) {
          this.showMessage = true;
          return true;
        }
        return false;
      }
    );

    if (hasEmptySecurity) {
      isValid = false;
      return;
    }
    const validatedPlans: any[] = [];

    for (let i = 0; i < this.tradingPlans.length; i++) {
      const planForm = this.tradingPlans.at(i) as FormGroup;

      if (planForm.invalid) {
        isValid = false;
        this._messageService.add({
          severity: "error",
          summary: "Error",
          detail: `Please fill all required fields in Trading Plan ${i + 1}`,
        });
        continue;
      }

      const planValue = planForm.value;
      const planId = planForm.get("tradingPlanId")?.value;
      const originalPlan =
        this.filing?.tradingPlans?.find(
          (p: any) => p.id === planId || p.tradingPlanId === planId
        ) || {};

      const breakdownArray = planForm.get("securityBreakdowns") as FormArray;
      const updatedBreakdowns: any[] = [];

      for (let j = 0; j < breakdownArray.length; j++) {
        const bdForm = breakdownArray.at(j) as FormGroup;

        if (bdForm.invalid) {
          isValid = false;
          this._messageService.add({
            severity: "error",
            summary: "Error",
            detail: `Please fill all required fields in Security Breakdown ${
              j + 1
            } of Trading Plan ${i + 1}`,
          });
          continue;
        }

        const bdValue = bdForm.value;
        const originalBreakdown =
          originalPlan.asReportedSecurityBreakdowns?.[j] || {};
        updatedBreakdowns.push({
          ...originalBreakdown,
          asReportedSecurityBreakdownId:
            originalBreakdown?.asReportedSecurityBreakdownId || undefined,
          transactionType: bdValue.transactionType || undefined,
          asReportedSecurity: {
            asReportedSecurityType: bdValue.securityType || undefined,
          },
          numberOfShares: bdValue.numberOfShares || undefined,
          notionalValue: bdValue.notionalValue || undefined,
          sharesDescription: bdValue.sharesDescription || undefined,
          securityBreakdownTags: (() => {
            let tags = bdValue.securityBreakdownTags || {};
            if (bdValue.transactionType === "BUY" && tags) {
              tags = { ...tags };
              delete (tags as any).sellToCover; //  yahan se bhi hata diya
            }
            return tags;
          })(),
        });
      }
      //  Auto-calculate expiration date if planAction is ADOPTION
      let calculatedExpirationDate = planValue.expirationDate;

      if (
        planValue.planAction === "ADOPTION" &&
        planValue.adoptionDate &&
        planValue.durationDays
      ) {
        const adoptionDate = new Date(planValue.adoptionDate);
        const duration = Number(planValue.durationDays);

        if (!isNaN(duration)) {
          const expiration = new Date(adoptionDate);
          expiration.setDate(adoptionDate.getDate() + duration);
          calculatedExpirationDate = expiration;
        }
      }

      // Create default modifiedFields object if it's null/undefined
      const planTradingPlanId = /^plan-[a-z0-9]+$/i.test(planId)
        ? undefined
        : planId;

      let modifiedFields = planValue.modifiedFields;

      // If modifiedFields is null/undefined, create default object for both new and existing plans
      if (!modifiedFields) {
        modifiedFields = {
          modifiedAdoptionDate: false,
          modifiedAmendmentDate: false,
          modifiedEffectiveDate: false,
          modifiedExpirationDate: false,
          modifiedNumberOfShares: false,
          modifiedPlanAction: false,
          modifiedTerminationDate: false,
          modifiedTransactionType: false,
          modifiedOther: false,
        };
        // Only add tradingPlanId if it exists (for existing plans)
        if (planTradingPlanId) {
          modifiedFields.tradingPlanId = planTradingPlanId;
        }
      } else if (modifiedFields && planTradingPlanId) {
        // If modifiedFields exists, ensure tradingPlanId is present (for existing plans)
        modifiedFields = {
          ...modifiedFields,
          tradingPlanId: planTradingPlanId,
        };
      }

      validatedPlans.push({
        tradingPlanId: planId || undefined,
        planAction: planValue.planAction || undefined,
        relationshipToIssuer: planValue.relation || undefined,
        adoptionDate:
          this.formatToDateString(planValue.adoptionDate) || undefined,
        effectiveDate:
          this.formatToDateString(planValue.effectiveDate) || undefined,
        amendmentDate:
          this.formatToDateString(planValue.amendmentDate) || undefined,
        terminationDate:
          this.formatToDateString(planValue.terminationDate) || undefined,
        expirationDate:
          this.formatToDateString(calculatedExpirationDate) || undefined,
        // expirationDate:
        //   this.formatToDateString(planValue.expirationDate) || undefined,
        durationDays: Number(planValue.durationDays) || undefined,
        asReportedPlanText: planValue.asReportedPlanText || undefined,
        earlyTerminationConditions:
          planValue.earlyTerminationConditions || undefined,
        tradingConditions: planValue.tradingConditions || undefined,
        modificationComment: planValue.modificationComment || undefined,
        modifiedFields: modifiedFields || undefined,
        isEstimatedAdoptionDate: planValue.isEstimatedAdoptionDate ?? undefined,
        isEstimatedTerminationDate:
          planValue.isEstimatedTerminationDate ?? undefined,
        isEstimatedAmendedDate:
          planValue.isEstimatedAmendedDate ?? undefined,
        asReportedInsider: {
          asReportedInsiderName: planValue.insiderName || undefined,
          asReportedReportingEntityName:
            planValue.asReportedReportingEntityName || null,
          asReportedReportingEntityType:
            planValue.asReportedReportingEntityType || null,
          asReportedInsiderRoleInEntity:
            planValue.asReportedInsiderRoleInEntity || null,
            insiderComment:
            planValue.insiderComment || "",
            insiderId:planValue.insiderId || null
        },
        asReportedSecurityBreakdowns: updatedBreakdowns,
      });
    }

    if (!isValid) return;

    const filing = {
      filingId: this.filing.filingId,
      asReportedIssuerId: this.filing.asReportedIssuerId,
      reason: "Plan Details Updated",
      followUpComment:
        this.formMainGroup.get("followUpComment")?.value || undefined,
      tradingPlans: validatedPlans,
    };

    this.isSaving = true;
    this._filingService.saveFiling(filing).subscribe({
      next: (response: boolean) => {
        if (response) {
          this.hasPersistedBreakdown = this.hasAnyBreakdownsInForm();
          this._messageService.add({
            severity: "success",
            summary: "Saved",
            detail: "Filing updated successfully",
          });
          this.ref.close({ refresh: true });
        } else {
          this._messageService.add({
            severity: "error",
            summary: "Save failed",
            detail: "Unable to save filing. Please try again.",
          });
        }
      },
      error: (err) => {
        this._messageService.add({
          severity: "error",
          summary: "Error",
          detail: "An error occurred while saving the filing.",
        });
        console.error("Error saving filing:", err);
      },
      complete: () => {
        this.isSaving = false;
      },
    });
  }
  AddTags(rowIndex: number, breakdownIndex: number): void {
    const breakdownArray = this.getBreakdownControls(rowIndex);
    const breakdown = breakdownArray.at(breakdownIndex) as FormGroup;
    const transactionType = breakdown.get("transactionType")?.value;
    let existingTags =
      (breakdown.get("securityBreakdownTags")
        ?.value as AsReportedSecurityBreakdownTags) ?? {};

    //  Agar BUY hai to sellToCover ko forcefully false kar do
    if (transactionType === "BUY" && existingTags.sellToCover) {
      existingTags = { ...existingTags, sellToCover: false };
      breakdown.get("securityBreakdownTags")?.setValue(existingTags);
    }
    const ref = this.dialogService.open(EditSecurityTagComponent, {
      width: "30vw",
      data: {
        existingTags: breakdown.get("securityBreakdownTags")?.value ?? {},
        transactionType,
      },
      modal: true,
      closable: true,
      header: "Add Tags",
    });

    ref.onClose.subscribe((selectedTags: AsReportedSecurityBreakdownTags) => {
      if (selectedTags) {
        breakdown.get("securityBreakdownTags")?.setValue(selectedTags);
        breakdown.markAsDirty();
      }
    });
  }

  markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      }
    });
  }

  onSelectionChange(event: any) {
    this.selectedPlans = event;
  }

  isFieldInvalid(formIndex: number, fieldName: string): boolean {
    const form = this.tradingPlans.at(formIndex) as FormGroup;
    const control = form.get(fieldName);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }

  isBreakdownFieldInvalid(
    rowIndex: number,
    breakdownIndex: number,
    fieldName: string
  ): boolean {
    const breakdownArray = this.getBreakdownControls(rowIndex);
    const breakdownGroup = breakdownArray.at(breakdownIndex) as FormGroup;
    const control = breakdownGroup.get(fieldName);
    return control
      ? control.invalid && (control.dirty || control.touched)
      : false;
  }

  getErrorMessage(formIndex: number, fieldName: string): string {
    const form = this.tradingPlans.at(formIndex) as FormGroup;
    const control = form.get(fieldName);
    if (control?.hasError("required")) {
      return "This field is required";
    }
    if (control?.hasError("min")) {
      return `Value must be greater than or equal to ${control.errors?.["min"].min}`;
    }
    if (control?.hasError("pattern")) {
      return "Please enter a valid number";
    }
    return "";
  }

  getBreakdownErrorMessage(
    rowIndex: number,
    breakdownIndex: number,
    fieldName: string
  ): string {
    const breakdownArray = this.getBreakdownControls(rowIndex);
    const breakdownGroup = breakdownArray.at(breakdownIndex) as FormGroup;
    const control = breakdownGroup.get(fieldName);
    if (control?.hasError("required")) {
      return "This field is required";
    }
    if (control?.hasError("min")) {
      return `Value must be greater than or equal to ${control.errors?.["min"].min}`;
    }
    if (control?.hasError("pattern")) {
      return "Please enter a valid number";
    }
    return "";
  }
  formatToDateString(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    return formatDate(d, "yyyy-MM-dd", "en-US");
  }
  // Trading Plan ke liye Tags function
  AddPlanTags(rowIndex: number): void {
    const planForm = this.tradingPlans.at(rowIndex) as FormGroup;
    const originalPlan = this.selectedPlans[rowIndex];

    // Get existing values from form or original plan
    const currentComment =
      planForm.get("modificationComment")?.value ||
      originalPlan?.modificationComment ||
      "";
    const existingModifiedFields =
      planForm.get("modifiedFields")?.value ||
      originalPlan?.modifiedFields ||
      null;
    const existingIsEstimatedAdoptionDate =
      planForm.get("isEstimatedAdoptionDate")?.value ??
      originalPlan?.isEstimatedAdoptionDate ??
      null;
    const existingIsEstimatedTerminationDate =
      planForm.get("isEstimatedTerminationDate")?.value ??
      originalPlan?.isEstimatedTerminationDate ??
      null;
    const existingIsEstimatedAmendedDate =
      planForm.get("isEstimatedAmendedDate")?.value ??
      originalPlan?.isEstimatedAmendedDate ??
      null;
    const tradingPlanId =
      planForm.get("tradingPlanId")?.value ||
      originalPlan?.tradingPlanId ||
      originalPlan?.id ||
      null;

    const ref = this.dialogService.open(AddTextComponent, {
      width: "30vw",
      modal: true,
      closable: true,
      header: "Edit Tag",
      data: {
        modificationComment: currentComment,
        modifiedFields: existingModifiedFields,
        isEstimatedAdoptionDate: existingIsEstimatedAdoptionDate,
        isEstimatedTerminationDate: existingIsEstimatedTerminationDate || "",
        isEstimatedAmendedDate: existingIsEstimatedAmendedDate || "",
        tradingPlanId: tradingPlanId,
        processedEdit: true,
      },
    });

    // When dialog closes, parse the JSON payload and update form fields
    ref.onClose.subscribe((payload: string) => {
      if (!payload) return;

      try {
        const parsed = JSON.parse(payload);

        // Update modificationComment
        if (!planForm.get("modificationComment")) {
          planForm.addControl("modificationComment", this.fb.control(""));
        }
        planForm.get("modificationComment")?.setValue(parsed?.comment || "");

        // Update modifiedFields
        if (!planForm.get("modifiedFields")) {
          planForm.addControl("modifiedFields", this.fb.control(null));
        }
        planForm
          .get("modifiedFields")
          ?.setValue(parsed?.modifiedFields ?? null);

        // Update isEstimatedAdoptionDate
        if (!planForm.get("isEstimatedAdoptionDate")) {
          planForm.addControl("isEstimatedAdoptionDate", this.fb.control(null));
        }
        planForm
          .get("isEstimatedAdoptionDate")
          ?.setValue(parsed?.is_estimated_adoption_date ?? null);

        // Update isEstimatedTerminationDate
        if (!planForm.get("isEstimatedTerminationDate")) {
          planForm.addControl(
            "isEstimatedTerminationDate",
            this.fb.control(null)
          );
        }
        planForm
          .get("isEstimatedTerminationDate")
          ?.setValue(parsed?.is_estimated_termination_date ?? null);

        // Update isEstimatedAmendedDate
        if (!planForm.get("isEstimatedAmendedDate")) {
          planForm.addControl("isEstimatedAmendedDate", this.fb.control(null));
        }
        planForm
          .get("isEstimatedAmendedDate")
          ?.setValue(parsed?.is_estimated_amended_date ?? null);

        planForm.markAsDirty();
      } catch {
        // If parsing fails, clear all fields
        if (planForm.get("modificationComment")) {
          planForm.get("modificationComment")?.setValue(null);
        }
        if (planForm.get("modifiedFields")) {
          planForm.get("modifiedFields")?.setValue(null);
        }
        if (planForm.get("isEstimatedAdoptionDate")) {
          planForm.get("isEstimatedAdoptionDate")?.setValue(null);
        }
        if (planForm.get("isEstimatedTerminationDate")) {
          planForm.get("isEstimatedTerminationDate")?.setValue(null);
        }
        if (planForm.get("isEstimatedAmendedDate")) {
          planForm.get("isEstimatedAmendedDate")?.setValue(null);
        }
      }
    });
  }

  private setupCloseGuard(): void {
    if (this.closeGuardInitialized || !this.ref) {
      return;
    }
    this.originalCloseFn = this.ref.close.bind(this.ref);
    this.ref.close = (result?: any) => {
      if (this.canDialogClose()) {
        this.originalCloseFn?.(result);
      } else {
        this.showCloseBlockedWarning();
      }
    };
    this.closeGuardInitialized = true;
  }

  private canDialogClose(): boolean {
    return this.hasPersistedBreakdown;
  }

  private showCloseBlockedWarning(): void {
    this._messageService.add({
      severity: "warn",
      summary: "Cannot close dialog",
      detail: "Add and save at least one security breakdown before closing.",
    });
  }

  private updatePersistedBreakdownFlagFromSelectedPlans(): void {
    if (!Array.isArray(this.selectedPlans)) {
      this.hasPersistedBreakdown = false;
      return;
    }
    this.hasPersistedBreakdown = this.selectedPlans.some(
      (plan) =>
        Array.isArray(plan?.asReportedSecurityBreakdowns) &&
        plan.asReportedSecurityBreakdowns.length > 0
    );
  }

  private removePersistedBreakdownReference(plan: any, index: number): void {
    if (Array.isArray(plan?.asReportedSecurityBreakdowns)) {
      plan.asReportedSecurityBreakdowns.splice(index, 1);
    }
    this.updatePersistedBreakdownFlagFromSelectedPlans();
  }

  private hasAnyBreakdownsInForm(): boolean {
    return this.tradingPlans.controls.some((control) => {
      const array = control.get("securityBreakdowns") as FormArray;
      return array && array.length > 0;
    });
  }
}
